import { useMemo } from "react";
import { useViewScope, type ViewScope } from "./ViewScopeProvider";

// Wave 20 — per-tab scope support registry.
//
// Most tabs will eventually honor the global scope, but a handful are fixed
// (Audit Dashboard is inherently company-wide; Tax Policies is scope-agnostic
// metadata). Rather than hard-coding these cases on each page, declare them
// here and let consumers ask `useEffectiveScopeForTab` for the resolved
// scope + whether it had to be pinned to a fallback.

export type TabKey =
  | "inbox"
  | "expenses"
  | "invoices"
  | "review"
  | "vendors"
  | "reports"
  | "exports"
  | "audit_dashboard"
  | "tax_policies"
  | "settings"
  | "integrations";

export interface TabScopeSupport {
  supportsMine: boolean;
  supportsCompany: boolean;
  /** If current scope is unsupported, tab falls back to this. */
  fallback: ViewScope;
}

export const TAB_SCOPE_SUPPORT: Record<TabKey, TabScopeSupport> = {
  inbox: { supportsMine: true, supportsCompany: true, fallback: "company" },
  expenses: { supportsMine: true, supportsCompany: true, fallback: "company" },
  invoices: { supportsMine: true, supportsCompany: true, fallback: "company" },
  review: { supportsMine: true, supportsCompany: true, fallback: "company" },
  vendors: { supportsMine: true, supportsCompany: true, fallback: "company" },
  reports: { supportsMine: true, supportsCompany: true, fallback: "company" },
  exports: { supportsMine: true, supportsCompany: true, fallback: "company" },
  audit_dashboard: {
    supportsMine: false,
    supportsCompany: true,
    fallback: "company",
  },
  // Tax Policies, Settings, and Integrations aren't scope-sensitive today.
  // Declaring both flags `true` keeps the helper a no-op for these tabs
  // (never pinned), which mirrors the UX — toggling the header pill
  // shouldn't visibly change anything when viewing them.
  tax_policies: {
    supportsMine: true,
    supportsCompany: true,
    fallback: "company",
  },
  settings: { supportsMine: true, supportsCompany: true, fallback: "company" },
  integrations: {
    supportsMine: true,
    supportsCompany: true,
    fallback: "company",
  },
};

export interface EffectiveTabScope {
  scope: ViewScope;
  /** True when the global scope isn't supported for this tab and we had to
   * fall back. Consumers can surface this in UI (e.g. a "scope overridden"
   * hint) so users aren't confused by the apparent no-op. */
  pinned: boolean;
}

/** Pure resolver — exported so non-hook callers (tests, fixtures) can share
 * the logic. */
export function resolveScopeForTab(
  global: ViewScope,
  tab: TabKey,
): EffectiveTabScope {
  const support = TAB_SCOPE_SUPPORT[tab];
  const globalSupported =
    (global === "mine" && support.supportsMine) ||
    (global === "company" && support.supportsCompany);
  if (globalSupported) {
    return { scope: global, pinned: false };
  }
  return { scope: support.fallback, pinned: true };
}

export function useEffectiveScopeForTab(tabKey: TabKey): EffectiveTabScope {
  const { scope } = useViewScope();
  return useMemo(() => resolveScopeForTab(scope, tabKey), [scope, tabKey]);
}
